import React, { useState } from 'react';
import { Box, Grid2 as Grid, Stack, Typography } from '@mui/material';
import { ShowContextProvider, ShowData } from 'framework/show';
import { createTruncatedTextFormat, FormattedTextField } from 'framework/ui/field';
import { PlusIcon, Tooltip } from 'framework/ui/mui-builder';
import {
  AssignMemberTimeField,
  FTEBadge,
  ProjectTeamIssuesField,
  ProjectTeamMemberPositionField,
  ProjectTeamMemberRoleField,
  ProjectTeamSlotPositionField,
  TeamMemberField,
} from 'domain/shared/newProject';
import IconButton from '@mui/material/IconButton';
import { AssignmentType, AssignmentPendingIndicator } from 'domain/shared/assignment';
import CreateStaffingAssignmentModal from '../createUpdate/create/CreateStaffingAssignmentModal';
import UpdateDeleteStaffingAssignmentModal from '../createUpdate/update/UpdateDeleteStaffingAssignmentModal';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import {
  AssignmentModalInitialData,
  SetStaffingProjectData,
  StaffingAssignmentListMember,
  StaffingAssignmentSlot,
  StaffingProjectData,
} from 'domain/shared/staffing';

const AssignmentSlot = ({
  slot,
  projectData,
  setProjectData,
}: {
  slot: StaffingAssignmentSlot;
  projectData: StaffingProjectData;
  setProjectData?: SetStaffingProjectData;
}) => {
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [selectedMemberForUpdate, setSelectedMemberForUpdate] = useState<StaffingAssignmentListMember | null>(null);
  const members = (slot?.members || []).filter(Boolean);
  const hasActiveAssignments = members.length > 0;

  const modalInitialData = {
    assignmentType: AssignmentType.INDIVIDUAL_ASSIGNMENT,
    slotId: slot.id,
    membershipRole: slot.requiredRole,
    positionId: slot.positionId,
    technologyId: slot.technologyId,
    competencyLevel: slot.competencyLevel,
  };

  const renderSlotMembers = () => {
    if (!hasActiveAssignments) {
      return (
        <Typography pt={0.5} variant="body2" color="textSecondary" pl={2}>
          No active assignments.
        </Typography>
      );
    }

    return members.map((member: StaffingAssignmentListMember, memberIndex: number) => {
      return (
        <ShowContextProvider
          key={member.id || `${member.employeeId}-${memberIndex}`}
          value={{
            data: new ShowData(member),
          }}
        >
          <Grid
            py={0.5}
            container
            alignItems="flex-start"
            direction="row"
            justifyContent="space-between"
            pr={0}
            columnSpacing={1}
            columns={13}
          >
            <Grid size={{ xs: 2.2 }} sx={{ pl: '20px' }}>
              <TeamMemberField source="employeeAlias" />
            </Grid>
            <Grid display="flex" alignItems="center" size={{ xs: 0.8 }}>
              <FTEBadge source="fte" isMemberFter minPercentSource="minPercent" maxPercentSource="maxPercent" />
            </Grid>
            <Grid size={{ xs: 2 }}>
              <ProjectTeamMemberPositionField
                positionIdSource="positionId"
                technologyIdSource="technologyId"
                competencyLevelSource="competencyLevel"
              />
            </Grid>
            <Grid size={{ xs: 2 }}>
              <ProjectTeamMemberRoleField label="" source="role" />
            </Grid>
            <Grid size={{ xs: 2 }}>
              <AssignMemberTimeField source="startDate" dateFromSource="startDate" dateToSource="endDate" />
            </Grid>
            <Grid size={{ xs: 2 }}>
              <FormattedTextField
                source="noteText"
                label=""
                format={(str: string) =>
                  str && str.length > 40 ? (
                    <Tooltip title={str}>
                      <div>
                        <Typography
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.2,
                            wordBreak: 'break-word',
                          }}
                        >
                          {str}
                        </Typography>
                      </div>
                    </Tooltip>
                  ) : (
                    str
                  )
                }
              />
            </Grid>
            <Grid size={{ xs: 1.5 }}>
              <AssignmentPendingIndicator source="isPending" />
              <ProjectTeamIssuesField source="issues" />
            </Grid>
            <Grid size={{ xs: 0.5 }} sx={{ display: 'flex', justifyContent: 'flex-end', pr: 1 }}>
              <Tooltip title={'Update assignment'}>
                <IconButton
                  onClick={() => {
                    setSelectedMemberForUpdate(member);
                  }}
                  sx={{ p: 0 }}
                >
                  <AppRegistrationIcon color="primary" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </ShowContextProvider>
      );
    });
  };

  return (
    <Box borderBottom="1px solid var(--mui-palette-TableCell-border)" py={1}>
      <Grid
        container
        key={`header`}
        alignItems="center"
        justifyContent="space-between"
        sx={{ pr: 0, pb: 0.5 }}
        columnSpacing={1}
        columns={13}
      >
        <ShowContextProvider value={{ data: new ShowData(slot) }}>
          <Grid
            size={{ xs: 2.2 }}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 0 }}
          >
            <ProjectTeamSlotPositionField
              positionIdSource="positionId"
              technologyIdSource="technologyId"
              competencyLevelSource="competencyLevel"
            />
            <Tooltip title={'Create assignment'}>
              <IconButton onClick={() => setCreateModalOpen(true)} sx={{ p: 0 }}>
                <PlusIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid size={{ xs: hasActiveAssignments ? 6.8 : 2.8 }}>
            <FTEBadge source="requiredFte" assignedFteSource="assignedFte" />
          </Grid>
          {!hasActiveAssignments && (
            <Grid size={{ xs: 4 }}>
              <ProjectTeamMemberRoleField label="" source="requiredRole" />
            </Grid>
          )}
          <Grid size={{ xs: 2 }}>
            <FormattedTextField source="description" label="" format={createTruncatedTextFormat(40)} />
          </Grid>
          <Grid size={{ xs: 2 }}>
            <ProjectTeamIssuesField source="issues" />
          </Grid>
        </ShowContextProvider>
      </Grid>
      <Stack>{renderSlotMembers()}</Stack>
      <CreateStaffingAssignmentModal
        open={isCreateModalOpen}
        setOpen={setCreateModalOpen}
        projectData={projectData}
        setProjectData={setProjectData}
        initialData={modalInitialData}
      />
      {selectedMemberForUpdate && (
        <UpdateDeleteStaffingAssignmentModal
          open={!!selectedMemberForUpdate}
          setOpen={open => {
            if (!open) {
              setSelectedMemberForUpdate(null);
            }
          }}
          projectData={projectData}
          setProjectData={setProjectData}
          initialData={
            {
              id: selectedMemberForUpdate.assignmentId,
              assignmentType: AssignmentType.INDIVIDUAL_ASSIGNMENT,
              employeeId: selectedMemberForUpdate.employeeId,
              slotId: selectedMemberForUpdate.slotId,
              membershipRole: selectedMemberForUpdate.membershipRole,
              positionId: selectedMemberForUpdate.positionId,
              technologyId: selectedMemberForUpdate.technologyId,
              competencyLevel: selectedMemberForUpdate.competencyLevel,
              minPercent: selectedMemberForUpdate.minPercent,
              maxPercent: selectedMemberForUpdate.maxPercent,
              backup: selectedMemberForUpdate.backup,
              isSpof: selectedMemberForUpdate.isSpof,
              bench: selectedMemberForUpdate.bench,
              from: selectedMemberForUpdate.from,
              to: selectedMemberForUpdate.to,
              noteText: selectedMemberForUpdate.noteText,
            } satisfies AssignmentModalInitialData
          }
        />
      )}
    </Box>
  );
};

export default AssignmentSlot;
